import {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	applyThemeToDocument,
	getStoredTheme,
	setStoredTheme,
	type Theme,
} from "@/utils/theme";

export interface ThemeContextInterface {
	theme: Theme;
	darkTheme: boolean;
	toggleTheme(): void;
}

export const ThemeContext = createContext<ThemeContextInterface | null>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
	const [theme, setTheme] = useState<Theme>(() => {
		const initialTheme = getStoredTheme();
		applyThemeToDocument(initialTheme);
		return initialTheme;
	});

	// Add 400ms transition for colors
	useEffect(() => {
		const addTransition = () => {
			const root = document.documentElement;
			root.style.transition = "background-color 400ms, color 400ms";
			document.body.style.transition = "background-color 400ms, color 400ms";
		};
		addTransition();
		// Clean up: remove transition style on unmount
		return () => {
			const root = document.documentElement;
			root.style.transition = "";
			document.body.style.transition = "";
		};
	}, []);

	// Toggle between dark and light theme
	const toggleTheme = useCallback(() => {
		const newTheme: Theme = theme === "dark" ? "light" : "dark";
		setStoredTheme(newTheme);
		setTheme(newTheme);
		applyThemeToDocument(newTheme);
	}, [theme]);

	// Listen for storage changes (cross-tab sync)
	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === "theme" && e.newValue) {
				const newTheme = e.newValue as Theme;
				if (newTheme === "dark" || newTheme === "light") {
					setTheme(newTheme);
					applyThemeToDocument(newTheme);
				}
			}
		};

		window.addEventListener("storage", handleStorageChange);

		return () => {
			window.removeEventListener("storage", handleStorageChange);
		};
	}, []);

	const contextValue = useMemo(
		() => ({
			theme,
			darkTheme: theme === "dark",
			toggleTheme,
		}),
		[theme, toggleTheme],
	);

	return (
		<ThemeContext.Provider value={contextValue}>
			{children}
		</ThemeContext.Provider>
	);
};
